// Small controls for a table's toolbar and filter panel, so every page's
// toolbar looks the same. They carry no page-specific behaviour.
import { cx } from "./utils";

/** Toolbar button. `variant="primary"` for the page's main action. */
export function DataTableButton({ children, icon: Icon, variant = "secondary", className, ...rest }) {
  return (
    <button
      type="button"
      className={cx(
        "inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-60",
        variant === "primary"
          ? "bg-blue-600 text-white hover:bg-blue-700"
          : "border border-app-primary bg-app-card text-app-secondary hover:bg-app-hover hover:text-app-primary",
        className,
      )}
      {...rest}
    >
      {Icon && <Icon size={16} strokeWidth={1.75} aria-hidden="true" />}
      {children}
    </button>
  );
}

/**
 * Labelled select for filter panels.
 * @param {{ id: string, label: string, value: string|number, onChange: (value: string) => void,
 *           options: { value: string|number, label: string }[] }} props
 */
export function DataTableSelect({ id, label, value, onChange, options, className }) {
  return (
    <div className={cx("flex min-w-[160px] flex-col gap-1.5", className)}>
      <label htmlFor={id} className="text-xs font-medium text-app-muted">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-full rounded-lg border border-app-primary bg-app-input py-0 pl-3 pr-8 text-sm text-app-primary focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Row of filter controls with a "Clear" button when anything is set. */
export function DataTableFilters({ children, onClear, canClear, clearLabel = "Clear filters" }) {
  return (
    <div className="flex flex-wrap items-end gap-4">
      {children}
      {onClear && canClear && (
        <button
          type="button"
          onClick={onClear}
          className="h-9 rounded-lg px-3 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 dark:text-blue-400 dark:hover:bg-blue-500/10"
        >
          {clearLabel}
        </button>
      )}
    </div>
  );
}
